import { REQUIRED_MESSAGE } from '@wyz/utils';
import { z } from 'zod';
import {
  DIAMETER_OPERATORS,
  OVERLAP_DIAMETER,
  PRICING_TYPE_ENUM,
  diameterSchema,
  validateRanges,
} from '~/purchase/schemas';
import { IDiameter, IPricingConfigurationType } from '~/purchase/types';

export const EXTRA_BONUS_MIN = 0;
export const EXTRA_BONUS_MAX = 100;

export const EXTRA_BONUS_OUT_OF_RANGE_MESSAGE =
  'app.common.validations.percent_field';

export const EXTRA_BONUS_NEGATIVE_MESSAGE =
  'app.common.validations.positive_number';

const isFilled = (value?: number | null): value is number =>
  null !== value && undefined !== value && !Number.isNaN(value);

/**
 * A discount and a margin are percentages, so they are bounded on both ends; a net margin is an amount
 * in the value's currency, which only has to stay positive. The percentage rule mirrors the one the API
 * enforces on the payload, so the save is refused here rather than by a round trip.
 */
const violationOf = (
  bonusType: IPricingConfigurationType,
  value?: number | null,
): string | null => {
  if (!isFilled(value)) {
    return null;
  }

  if (PRICING_TYPE_ENUM.NET_MARGIN === bonusType) {
    return value < EXTRA_BONUS_MIN ? EXTRA_BONUS_NEGATIVE_MESSAGE : null;
  }

  return value < EXTRA_BONUS_MIN || value > EXTRA_BONUS_MAX
    ? EXTRA_BONUS_OUT_OF_RANGE_MESSAGE
    : null;
};

export const distributionExtraBonusDraftSchema = z
  .object({
    bonusType: z.enum(PRICING_TYPE_ENUM),
    hqBonus: z.number().nullable(),
    values: z.record(z.string(), z.number().nullable()),
  })
  .superRefine((draft, ctx) => {
    const hqBonusViolation = violationOf(draft.bonusType, draft.hqBonus);

    if (hqBonusViolation) {
      ctx.addIssue({
        code: 'custom',
        path: ['hqBonus'],
        message: hqBonusViolation,
      });
    }

    Object.entries(draft.values ?? {}).forEach(([key, value]) => {
      const violation = violationOf(draft.bonusType, value);

      if (violation) {
        ctx.addIssue({
          code: 'custom',
          path: ['values', key],
          message: violation,
        });
      }
    });
  });

/**
 * The advanced entry targets at least one brand and one source, since a bonus is stored on a brand and
 * read through a supplier type. The amounts follow the same bounds as the simple mode, checked against
 * the type of the bonus, and the diameter rows follow the rule the quote pricing policies already
 * enforce: never the same diameter twice.
 */
/** The bounds a diameter row needs are the ones its operator reads. */
const isComplete = (diameter: IDiameter): boolean => {
  if (DIAMETER_OPERATORS.LOWER_THAN_OR_EQUAL === diameter.operator) {
    return isFilled(diameter.to);
  }

  if (DIAMETER_OPERATORS.BETWEEN === diameter.operator) {
    return isFilled(diameter.from) && isFilled(diameter.to);
  }

  return isFilled(diameter.from);
};

export const advancedExtraBonusDraftSchema = z
  .object({
    brands: z.array(z.string()).min(1, REQUIRED_MESSAGE),
    supplierTypeIds: z.array(z.number()).min(1, REQUIRED_MESSAGE),
    hqBonusType: z.enum(PRICING_TYPE_ENUM),
    hqBonus: z.number().nullable(),
    bonusType: z.enum(PRICING_TYPE_ENUM),
    byVehicleType: z.boolean(),
    byDiameter: z.boolean(),
    diameters: z.array(z.object({ diameter: diameterSchema })),
    values: z.record(z.string(), z.number().nullable()),
  })
  .superRefine((draft, ctx) => {
    const hqBonusViolation = violationOf(draft.hqBonusType, draft.hqBonus);

    if (hqBonusViolation) {
      ctx.addIssue({
        code: 'custom',
        path: ['hqBonus'],
        message: hqBonusViolation,
      });
    }

    Object.entries(draft.values ?? {}).forEach(([key, value]) => {
      const violation = violationOf(draft.bonusType, value);

      if (violation) {
        ctx.addIssue({
          code: 'custom',
          path: ['values', key],
          message: violation,
        });
      }
    });

    if (!draft.byDiameter) {
      return;
    }

    const diameters = (draft.diameters ?? [])
      .map((row) => row?.diameter)
      .filter((diameter) => null !== diameter && undefined !== diameter);

    if (0 === diameters.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['diameters'],
        message: REQUIRED_MESSAGE,
      });

      return;
    }

    // A row still missing the bound its operator reads is reported by the row itself, and comparing
    // it would throw rather than report anything.
    if (!diameters.every(isComplete)) {
      return;
    }

    // A bonus may well cover part of the diameters only, but never the same one twice: an offer
    // matching two of its rules has no amount the screen could show.
    const { overlap } = validateRanges(diameters);

    if (overlap) {
      ctx.addIssue({
        code: 'custom',
        path: ['diameters'],
        message: OVERLAP_DIAMETER,
      });
    }
  });
